import React, { useState, useEffect } from 'react';
import { Users, MessageCircle, Clock, CircleCheck as CheckCircle, Circle as XCircle, Loader, Phone } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';

interface Lead {
  id: string;
  client_name: string;
  cpf: string;
  phone: string;
  status: string;
  rejection_reason: string | null;
  first_contact_at: string | null;
  converted_at: string | null;
  created_at: string;
  contract_id: string | null;
}

type Tab = 'distributed' | 'in_contact' | 'awaiting_review' | 'approved' | 'rejected_seller';

export default function MyLeads() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('distributed');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState<Record<Tab, number>>({ distributed: 0, in_contact: 0, awaiting_review: 0, approved: 0, rejected_seller: 0 });
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchLeads = async () => {
    if (!user) return;
    setLoading(true);

    const { data } = await supabase
      .from('leads')
      .select('*')
      .eq('seller_id', user.id)
      .eq('status', activeTab)
      .order('created_at', { ascending: false });

    setLeads(data || []);
    setLoading(false);
  };

  const fetchCounts = async () => {
    if (!user) return;
    const tabs: Tab[] = ['distributed', 'in_contact', 'awaiting_review', 'approved', 'rejected_seller'];
    const newCounts: Record<string, number> = {};

    for (const tab of tabs) {
      const { count } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('seller_id', user.id)
        .eq('status', tab);
      newCounts[tab] = count || 0;
    }

    setCounts(newCounts as Record<Tab, number>);
  };

  useEffect(() => { fetchLeads(); }, [activeTab, user]);
  useEffect(() => { fetchCounts(); }, [user]);

  const handleStartContact = async (lead: Lead) => {
    const { error } = await supabase
      .from('leads')
      .update({ status: 'in_contact', first_contact_at: new Date().toISOString() })
      .eq('id', lead.id);

    if (!error) {
      window.open(`https://wa.me/55${lead.phone}`, '_blank');
      fetchLeads();
      fetchCounts();
    }
  };

  const handleOpenWhatsapp = (lead: Lead) => {
    window.open(`https://wa.me/55${lead.phone}`, '_blank');
  };

  const handleReject = async () => {
    if (!rejectingId || !rejectReason.trim()) return;

    const { error } = await supabase
      .from('leads')
      .update({
        status: 'rejected_seller',
        rejection_reason: rejectReason.trim(),
        rejected_by: user?.id,
      })
      .eq('id', rejectingId);

    if (!error) {
      setRejectingId(null);
      setRejectReason('');
      fetchLeads();
      fetchCounts();
    }
  };

  const maskCpf = (cpf: string) => {
    if (cpf.length === 11) return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.***-**`;
    return cpf;
  };

  const tabs: { key: Tab; label: string; icon: React.ElementType }[] = [
    { key: 'distributed', label: 'Novos', icon: Users },
    { key: 'in_contact', label: 'Em Atendimento', icon: MessageCircle },
    { key: 'awaiting_review', label: 'Aguardando Revisao', icon: Clock },
    { key: 'approved', label: 'Aprovados', icon: CheckCircle },
    { key: 'rejected_seller', label: 'Recusados', icon: XCircle },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Meus Leads</h1>

      {/* Tabs */}
      <div className="flex overflow-x-auto gap-1 bg-gray-100 p-1 rounded-xl">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${
              activeTab === tab.key
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {counts[tab.key] > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab.key ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'
              }`}>
                {counts[tab.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          Nenhum lead nesta categoria.
        </div>
      ) : (
        <div className="grid gap-3">
          {leads.map(lead => (
            <div key={lead.id} className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{lead.client_name}</p>
                <p className="text-sm text-gray-500">CPF: {maskCpf(lead.cpf)}</p>
                <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                  <span className="flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" />
                    {lead.phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')}
                  </span>
                  <span>{format(new Date(lead.created_at), 'dd/MM/yyyy HH:mm')}</span>
                </div>
                {lead.first_contact_at && (
                  <p className="text-xs text-gray-400 mt-1">
                    1o contato: {format(new Date(lead.first_contact_at), 'dd/MM/yyyy HH:mm')}
                  </p>
                )}
                {lead.rejection_reason && (
                  <p className="text-xs text-red-500 mt-1">Motivo: {lead.rejection_reason}</p>
                )}
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {activeTab === 'distributed' && (
                  <button
                    onClick={() => handleStartContact(lead)}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition"
                  >
                    Iniciar Atendimento
                  </button>
                )}
                {activeTab === 'in_contact' && (
                  <>
                    <button
                      onClick={() => handleOpenWhatsapp(lead)}
                      className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setRejectingId(lead.id)}
                      className="px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 text-sm font-medium rounded-lg transition"
                    >
                      Recusar
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reject Modal */}
      {rejectingId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Recusar Lead</h3>
            <p className="text-sm text-gray-600 mb-4">Informe o motivo da recusa. Este motivo sera visivel para o gestor.</p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Motivo da recusa..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => { setRejectingId(null); setRejectReason(''); }}
                className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim()}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition disabled:opacity-50"
              >
                Confirmar Recusa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
